import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import './intro.scss';

interface KeyRow {
  num: string;
  title: string;
  en: string;
  desc: string;
  egHead: string;
  egRest: string;
  on?: boolean;
}

type Visibility = 'on' | 'half' | 'off';

interface VisRow {
  item: string;
  sub: string;
  cells: [Visibility, Visibility, Visibility];
}

const facts = [
  { dt: '주소', dd: <>classicker.com/<i>이름</i> — 한 번 정하면 바뀌지 않습니다</> },
  { dt: '구성', dd: '여섯 개의 칸 · 모두 선택적으로 채웁니다' },
  { dt: '소요', dd: '첫 프로필 10분 · 이후엔 무대가 끝날 때마다 1분' },
  { dt: '공개', dd: '칸마다 따로 설정 · 기본값은 링크를 아는 사람만' },
];

const keyRows: KeyRow[] = [
  {
    num: '01', title: '레퍼토리', en: 'Repertoire',
    desc: '연주할 수 있는 곡을 카탈로그 번호와 함께 쌓습니다. 곡마다 상태를 붙여 “무대에 올릴 수 있음”과 “지금 공부 중”을 구분합니다.',
    egHead: 'BWV 1004', egRest: ' — Bach, Partita No. 2 · 연주 · 31분',
  },
  {
    num: '02', title: '연주 이력', en: 'Engagements',
    desc: '날짜·장소·역할·함께한 사람이 한 줄에 남습니다. 프로그램은 그 무대에서 연주한 곡과 자동으로 연결됩니다.',
    egHead: '2026.10.05', egRest: ' — 금호아트홀 연세 · 독주회',
  },
  {
    num: '03', title: '이번 시즌', en: 'Season',
    desc: '다가오는 무대 세 개가 프로필 맨 위에 뜹니다. 지난 이력만 있는 페이지와 살아 있는 페이지를 가르는 차이입니다.',
    egHead: '11.14', egRest: ' — 베토벤 소나타 전곡 Ⅲ',
  },
  {
    num: '04', title: '녹음 · 영상', en: 'Audio',
    desc: '곡에 붙는 라이브·스튜디오 녹음. 프로그램을 보내기 전에 소리로 판단할 수 있게 합니다. 외부 링크도 그대로 연결됩니다.',
    egHead: '샤콘느', egRest: ' — 2026.10.05 라이브 · 14:32',
  },
  {
    num: '05', title: '연주 가능 상태', en: 'Availability',
    desc: '협연·실내악·레슨을 지금 받을 수 있는지, 어느 지역과 시기인지. 연락해도 되는 사람인지 판단하는 데 쓰입니다.',
    egHead: '협연 가능', egRest: ' — 2026.12 이후 · 서울 / 경기',
  },
  {
    num: '06', title: '소개와 악기', en: 'About',
    desc: '사사, 악기, 언어, 앙상블 소속. 짧아도 됩니다. 이 칸은 검색과 추천에 쓰이는 메타데이터이기도 합니다.',
    egHead: 'G. B. Guadagnini, 1782', egRest: ' · 앙상블 오르페우스',
    on: true,
  },
];

const roleGroups = [
  { span: '01 — 02', b: '쌓는 칸', em: '시간이 지날수록 값이 올라갑니다' },
  { span: '03', b: '지금을 보여주는 칸', em: '페이지가 살아 있는지를 알려줍니다' },
  { span: '04 — 05', b: '판단을 돕는 칸', em: '소리와 조건을 먼저 확인시킵니다' },
  { span: '06', b: '맥락을 주는 칸', em: '누구인지 한 줄로 설명합니다' },
];

const steps = [
  {
    n: 'STEP 01', time: '3분', title: '악기와 소속', tag: '필수',
    desc: '전공 악기, 재학·졸업 여부, 소속 앙상블. 이 세 줄로 프로필 주소와 기본 골격이 만들어집니다.',
  },
  {
    n: 'STEP 02', time: '5분', title: '레퍼토리 10곡', tag: '권장 10곡',
    desc: '작곡가 이름으로 검색하면 카탈로그 번호와 소요 시간이 함께 붙습니다. 열 곡부터 링크가 쓸 만해집니다.',
  },
  {
    n: 'STEP 03', time: '2분', title: '첫 무대 하나', tag: '1건',
    desc: '지난 연주든 다가오는 연주든 한 건만 넣으면 페이지가 살아 있는 문서가 됩니다.',
  },
];

const visRows: VisRow[] = [
  { item: '이름 · 악기 · 소속', sub: '프로필의 뼈대', cells: ['off', 'on', 'on'] },
  { item: '레퍼토리와 상태', sub: '“지금 공부 중”까지 포함', cells: ['on', 'on', 'half'] },
  { item: '연주 이력 · 시즌 일정', sub: '장소와 함께한 사람', cells: ['on', 'on', 'on'] },
  { item: '녹음 · 영상', sub: '외부 링크 포함', cells: ['on', 'half', 'half'] },
  { item: '연주 가능 상태', sub: '연락 창구', cells: ['on', 'on', 'half'] },
  { item: '연락처', sub: '이메일 · 메시지', cells: ['on', 'half', 'off'] },
];

const shares = [
  {
    title: '프로필 링크',
    desc: '메일·문자·프로필란 어디에든. 상대는 로그인 없이 열람하고, 필요하면 PDF로 저장합니다.',
    code: 'classicker.com/yoon.seoha',
    icon: (
      <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M6.5 9.5a2.5 2.5 0 003.5 0l2-2a2.5 2.5 0 00-3.5-3.5l-1 1" stroke="currentColor" strokeWidth="1.2" /><path d="M9.5 6.5a2.5 2.5 0 00-3.5 0l-2 2a2.5 2.5 0 003.5 3.5l1-1" stroke="currentColor" strokeWidth="1.2" /></svg>
    ),
  },
  {
    title: '프로그램 PDF',
    desc: '기획자에게 보내는 용도. 연주 이력과 레퍼토리를 A4 두 장으로 정리해 내려받습니다.',
    code: 'yoon-seoha_2026-27.pdf',
    icon: (
      <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><path d="M3 2h7l3 3v9H3z" stroke="currentColor" strokeWidth="1.2" /><path d="M5.5 8h5M5.5 10.5h5" stroke="currentColor" strokeWidth="1.1" /></svg>
    ),
  },
  {
    title: '무대 앞 QR',
    desc: '리사이틀 프로그램북이나 포스터에 인쇄. 공연이 끝난 뒤 관객이 그 자리에서 프로필로 옵니다.',
    code: '프로그램북 3면 · 22mm',
    icon: (
      <svg width="15" height="15" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="4.5" height="4.5" stroke="currentColor" strokeWidth="1.1" /><rect x="9.5" y="2" width="4.5" height="4.5" stroke="currentColor" strokeWidth="1.1" /><rect x="2" y="9.5" width="4.5" height="4.5" stroke="currentColor" strokeWidth="1.1" /><path d="M9.5 9.5h2v2h-2zM12 12h2v2h-2z" fill="currentColor" /></svg>
    ),
  },
];

const faqs = [
  {
    q: '아마추어도 프로필을 만들 수 있나요?',
    a: <>네. 클래시커는 전공자만을 위한 곳이 아닙니다. 전공 여부는 소개 칸의 한 줄로 표시될 뿐, 검색 순위나 기능에 영향을 주지 않습니다. 오히려 <b>취미로 오래 연주해온 사람의 레퍼토리</b>가 실내악 모집에서 더 유용한 경우가 많습니다.</>,
  },
  {
    q: '레퍼토리를 전부 다 올려야 하나요?',
    a: <>아니요. 열 곡이면 충분히 쓸 만해지고, 자주 연주하는 곡부터 넣는 편이 좋습니다. <b>“지금 공부 중” 상태</b>로 넣어두면 나중에 무대가 잡혔을 때 연주 기록으로 바꾸기만 하면 됩니다.</>,
  },
  {
    q: '이미 쓰고 있는 링크가 있는데 옮겨야 하나요?',
    a: <>아니요. 프로필에서 외부 링크(개인 사이트, 유튜브, 인스타그램)를 그대로 연결할 수 있습니다. 클래시커는 그 링크들을 대체하는 것이 아니라, <b>곡과 무대라는 구조를 붙이는</b> 쪽입니다.</>,
  },
  {
    q: '연주 이력이 없는 학생은요?',
    a: <>레퍼토리와 학교·사사 정보만으로도 프로필은 성립합니다. 시즌 칸은 비워두면 페이지에 나오지 않습니다. 콩쿠르·정기연주회·앙상블 활동도 모두 이력으로 넣을 수 있습니다.</>,
  },
  {
    q: '곡 정보는 직접 입력해야 하나요?',
    a: <>작곡가와 곡 이름 일부만 입력하면 카탈로그 번호, 조성, 대략적인 소요 시간이 <b>자동으로 채워집니다.</b> 직접 고칠 수 있고, 편곡이나 발췌 연주는 별도로 표시할 수 있습니다.</>,
  },
  {
    q: '프로필을 지우면 기록도 사라지나요?',
    a: <>프로필은 언제든 비공개로 돌리거나 삭제할 수 있습니다. 삭제하면 링크는 즉시 열리지 않고, 남아 있던 데이터는 30일 뒤 완전히 파기됩니다. <b>PDF로 내려받은 사본</b>은 사용자가 보관한 파일이므로 별개입니다.</>,
  },
];

function BrandMark({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 26 26" fill="none" aria-hidden="true">
      <path d="M3 19c0-6.6 4.5-12 10-12s10 5.4 10 12" stroke="#0B0B0A" strokeWidth="1.3" />
      <path d="M13 7V3" stroke="#0B0B0A" strokeWidth="1.3" />
      <circle cx="13" cy="20.5" r="2.4" fill="#D9902A" />
    </svg>
  );
}

export default function Intro() {

  const pageRef = useRef<HTMLDivElement>(null);
  const [userName, setUserName] = useState<string | null>(null);

  // 로그인 상태 확인
  useEffect(() => {
    axios.get('/api/auth/me')
      .then((res) => setUserName(res.data.user.name))
      .catch(() => setUserName(null));
  }, []);

  const handleLogout = async () => {
    await axios.post('/api/auth/logout').catch(() => {});
    setUserName(null);
  };

  // 스크롤 시 등장 애니메이션
  useEffect(() => {
    const rvs = pageRef.current?.querySelectorAll('.rv');
    if (!rvs) return;
    if ('IntersectionObserver' in window && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.classList.add('in');
            io.unobserve(en.target);
          }
        });
      }, { threshold: 0.1, rootMargin: '0px 0px -40px 0px' });
      rvs.forEach((el) => io.observe(el));
      return () => io.disconnect();
    }
    rvs.forEach((el) => el.classList.add('in'));
  }, []);

  return (
    <div className="intro-page" ref={pageRef}>

      <header className="nav">
        <div className="nav-in">
          <Link className="brand" to="/">
            <BrandMark size={24} />
            <span className="brand-txt">
              <span className="brand-kr">클래시커</span>
              <span className="brand-lat">CLASSICKER</span>
            </span>
          </Link>
          <nav className="nav-links">
            <Link to="/intro" aria-current="page">프로필</Link>
            <Link to="/profile">샘플 프로필</Link>
          </nav>
          <div className="nav-right">
            {userName ? (
              <>
                <Link className="link-quiet" to="/mypage">마이페이지</Link>
                <button className="link-quiet link-btn" type="button" onClick={handleLogout}>로그아웃</button>
              </>
            ) : (
              <>
                <Link className="link-quiet" to="/login">로그인</Link>
                <Link className="btn btn-primary" to="/signup">회원가입</Link>
              </>
            )}
          </div>
        </div>
      </header>

      <main id="top">

        {/* ---------------- head ---------------- */}
        <section className="head">
          <div className="wrap">
            <p className="crumb"><span>클래시커</span> / <b>프로필</b></p>
            <div className="head-grid">
              <div>
                <h1>프로필은 한 장이지만,<br />들어 있는 것은 <em>커리어 전체</em>입니다.</h1>
                <p className="lede">
                  클래시커의 프로필은 자기소개 페이지가 아닙니다.{' '}
                  <b>무엇을 연주할 수 있는지</b>를 곡 단위로 증명하는 문서입니다.
                  링크 하나를 보내면 상대는 묻지 않고 알 수 있습니다.
                </p>
              </div>
              <dl className="head-facts">
                {facts.map((f) => (
                  <div className="hf" key={f.dt}><dt>{f.dt}</dt><dd>{f.dd}</dd></div>
                ))}
              </dl>
            </div>
          </div>
        </section>

        {/* ---------------- anatomy ---------------- */}
        <section className="anat">
          <div className="wrap">
            <div className="anat-head">
              <span>Anatomy of a Profile</span>
              <b>Fig. 1</b>
            </div>

            <div className="anat-grid">
              <div className="key">
                {keyRows.map((row) => (
                  <div className={`key-row rv${row.on ? ' on' : ''}`} key={row.num}>
                    <span className="mk">{row.num}</span>
                    <div>
                      <div className="key-t">{row.title} <span>{row.en}</span></div>
                      <p className="key-d">{row.desc}</p>
                      <div className="key-eg"><b>{row.egHead}</b>{row.egRest}</div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="specimen">
                <div className="sp">
                  <div className="sp-bar"><i></i><i></i><i></i><span>classicker.com/yoon.seoha</span></div>
                  <div className="sp-body">
                    <div className="sp-name">윤서하</div>
                    <div className="sp-role">Violin · 앙상블 오르페우스</div>

                    <div className="sp-sec">
                      <span className="pin">01</span>
                      <div className="sp-lbl">레퍼토리 · 128</div>
                      <div className="sp-line"><em>BWV 1004</em><span>Partita No. 2 — Chaconne</span></div>
                      <div className="sp-line"><em>Op. 24</em><span>Sonata No. 5 “Spring”</span></div>
                      <div className="sp-line"><em>Op. 61</em><span>Violin Concerto in D</span></div>
                      <div className="sp-tagrow"><span className="sp-tag a">연주 96</span><span className="sp-tag">준비 21</span><span className="sp-tag">학습 11</span></div>
                    </div>

                    <div className="sp-sec">
                      <span className="pin">02</span>
                      <div className="sp-lbl">연주 이력 · 42</div>
                      <div className="sp-line"><em>2026.10</em><span>금호아트홀 · 독주회</span></div>
                      <div className="sp-line"><em>2026.06</em><span>예술의전당 · 협연</span></div>
                    </div>

                    <div className="sp-sec">
                      <span className="pin">03</span>
                      <div className="sp-lbl">이번 시즌</div>
                      <div className="sp-line"><em>11.14</em><span>베토벤 소나타 Ⅲ</span></div>
                      <div className="sp-line"><em>12.02</em><span>라벨 4중주</span></div>
                    </div>

                    <div className="sp-sec">
                      <span className="pin">04</span>
                      <div className="sp-lbl">녹음 · 영상 · 9</div>
                      <div className="sp-line"><em>14:32</em><span>샤콘느 (라이브)</span></div>
                    </div>

                    <div className="sp-sec">
                      <span className="pin">05</span>
                      <div className="sp-lbl">연주 가능</div>
                      <div className="sp-tagrow"><span className="sp-tag a">협연</span><span className="sp-tag a">실내악</span><span className="sp-tag">레슨 불가</span></div>
                    </div>

                    <div className="sp-sec">
                      <span className="pin">06</span>
                      <div className="sp-lbl">소개와 악기</div>
                      <div className="sp-line"><em>악기</em><span>Guadagnini, 1782</span></div>
                    </div>
                  </div>
                </div>
                <p className="sp-cap">Fig. 1 — 프로필의 여섯 칸</p>
                <p className="sp-note">칸은 순서대로 채우지 않아도 됩니다. 레퍼토리만 있어도 링크를 보낼 수 있고, 나머지는 무대가 생길 때마다 하나씩 붙습니다.</p>

                <div className="sp-group">
                  <div className="sp-group-t">여섯 칸의 역할</div>
                  {roleGroups.map((g) => (
                    <div className="sg-row" key={g.span}><span>{g.span}</span><b>{g.b}</b><em>{g.em}</em></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ---------------- fill steps ---------------- */}
        <section className="sec">
          <div className="wrap">
            <div className="sec-rule">
              <span className="sec-lbl">채우는 방법</span>
              <span className="sec-lbl">01 / 4</span>
            </div>
            <h2 className="rv">세 번의 입력, 그 다음은 습관</h2>
            <p className="sec-desc rv">처음 만들 때 필요한 건 세 가지뿐입니다. 나머지는 연주가 있을 때마다 한 줄씩 늘어납니다.</p>

            <div className="steps rv">
              {steps.map((s) => (
                <div className="step" key={s.n}>
                  <div className="step-n"><span>{s.n}</span><span>{s.time}</span></div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                  <span className="step-time">{s.tag}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------------- visibility ---------------- */}
        <section className="sec sec-tight">
          <div className="wrap">
            <div className="sec-rule">
              <span className="sec-lbl">공개 범위</span>
              <span className="sec-lbl">02 / 4</span>
            </div>
            <h2 className="rv">무엇을 누구에게 보여줄지는 칸마다 정합니다</h2>
            <p className="sec-desc rv">프로필은 하나지만 보이는 방식은 여러 가지입니다. 기본값은 <b>링크를 아는 사람만</b> — 검색에는 노출되지 않습니다.</p>

            <table className="vtab rv">
              <thead>
                <tr>
                  <th>항목</th>
                  <th>나만</th>
                  <th>링크 공유</th>
                  <th>전체 공개</th>
                </tr>
              </thead>
              <tbody>
                {visRows.map((row) => (
                  <tr key={row.item}>
                    <td>{row.item}<span>{row.sub}</span></td>
                    {row.cells.map((c, i) => (
                      <td key={i}><span className={`dot-${c}`}></span></td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="vlegend">
              <span><i className="dot-on"></i>직접 설정</span>
              <span><i className="dot-half"></i>설정에 따라</span>
              <span><i className="dot-off"></i>불가</span>
            </div>
          </div>
        </section>

        {/* ---------------- share ---------------- */}
        <section className="sec sec-tight">
          <div className="wrap">
            <div className="sec-rule">
              <span className="sec-lbl">건네는 방식</span>
              <span className="sec-lbl">03 / 4</span>
            </div>
            <h2 className="rv">링크 하나, 종이 한 장, 무대 앞의 QR</h2>
            <p className="sec-desc rv">같은 프로필이 상황에 따라 다른 모습으로 나갑니다. 상대가 앱을 설치하지 않아도 열립니다.</p>

            <div className="share-grid rv">
              {shares.map((s) => (
                <div className="share-card" key={s.title}>
                  <div className="share-ico">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                  <code>{s.code}</code>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ---------------- black band ---------------- */}
        <section className="band">
          <div className="wrap">
            <div className="band-rule">
              <span>Programme Note</span>
              <span>No. 02</span>
            </div>
            <div className="band-grid">
              <p className="band-q">프로필은 <em>연주자의 악기</em>입니다.</p>
              <div className="band-body">
                <p>연주자는 자기 소리를 악기로 증명합니다. 하지만 악기를 켜보기 전에는 아무것도 알 수 없고, 모든 사람에게 켜보게 할 수도 없습니다. 그 사이를 메우는 것이 기록입니다.</p>
                <p>그래서 클래시커의 프로필은 이력서처럼 위에서 아래로 읽히는 문서가 아니라, 질문이 생길 때마다 곡 단위로 파고들 수 있는 구조입니다. “이 사람과 브람스 할 수 있나”, “12월에 서울에서 가능한가” — 이런 질문에 한 번에 답이 나오도록 만들었습니다.</p>
                <ul className="band-list">
                  <li>곡 하나를 누르면 그 곡을 연주한 무대와 녹음이 함께 나옵니다</li>
                  <li>레퍼토리는 앙상블·악기·난이도로 다시 걸러집니다</li>
                  <li>링크를 받은 사람은 앱을 설치하지 않아도 됩니다</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* ---------------- faq ---------------- */}
        <section className="sec">
          <div className="wrap">
            <div className="sec-rule">
              <span className="sec-lbl">자주 묻는 것</span>
              <span className="sec-lbl">04 / 4</span>
            </div>

            <div className="faq rv">
              {faqs.map((f, i) => (
                <details key={f.q} open={i === 0}>
                  <summary>{f.q}</summary>
                  <div className="faq-body">{f.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* ---------------- cta ---------------- */}
        <section className="cta">
          <div className="wrap">
            <h2 className="rv">여섯 칸, 첫 곡부터.</h2>
            <p className="rv">베타 기간에는 프로필 생성과 공개가 무료입니다.</p>
            <div className="cta-row rv">
              <Link className="btn btn-primary btn-lg" to={userName ? '/profile/new' : '/signup'}>프로필 만들기 — 무료</Link>
              <Link className="btn btn-ghost btn-lg" to="/profile">샘플 프로필 보기</Link>
            </div>
          </div>
        </section>

      </main>

      <footer className="foot">
        <div className="wrap">
          <div className="foot-grid">
            <div className="foot-brand">
              <Link className="brand" to="/">
                <BrandMark size={22} />
                <span className="brand-txt"><span className="brand-kr">클래시커</span><span className="brand-lat">CLASSICKER</span></span>
              </Link>
              <p>클래식을 연주하고 듣는 사람들의 프로필.</p>
            </div>
            <div className="foot-col">
              <h4>서비스</h4>
              <Link to="/intro">프로필</Link>
              <a href="#top">앙상블</a>
              <a href="#top">공연 일정</a>
            </div>
            <div className="foot-col">
              <h4>둘러보기</h4>
              <Link to="/profile">샘플 프로필</Link>
              <a href="#top">연주자 찾기</a>
              <a href="#top">이번 주 공연</a>
            </div>
            <div className="foot-col">
              <h4>고지</h4>
              <a href="#top">이용약관</a>
              <a href="#top">개인정보 처리방침</a>
              <a href="#top">문의</a>
            </div>
          </div>
          <div className="foot-bottom">
            <span>© 2026 CLASSICKER</span>
            <span>기획 프로토타입 화면입니다 · 인물·공연·수치는 예시</span>
          </div>
        </div>
      </footer>

    </div>
  );
}
